import React, { useEffect, useMemo, useState } from "react";
import PageContainer from "../../components/PageContainer";
import MainContent from "../../components/MainContent";
import PageTitle from "../../components/Common/PageTitle/PageTitle";
import ActionDropdownItem from "../../components/Common/PageTitle/ActionDropdownItem";
import { useIncomingInvoice } from "../../DataStore/IncomingInvoiceContext";
import ActivityLoader from "../../components/Common/ActivityLoader";
import Notification from "../../components/Common/Notification";
import functions from "../../util/functions";
import Pagination from "../../components/Common/Pagination";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../DataStore/ThemeContext";
import AlertNotification from "../../components/Common/AlertNotification";
import AlertDeleteLedger from "../../components/Ledger/AlertDeleteLedger";
import FilterBar from "../../components/Common/FilterBar"; // ✅ adjust path to your FilterBar

const SupplierInvoicingPage = () => {
  const { getInvoiceTransactions, incomingInvoiceListing, metaData, setCurrentPage, refresh } =
    useIncomingInvoice();

  const [searchText, setSearchText] = useState("");
  const [selected, setSelected] = useState(null);
  const { openSettings, setNotify } = useTheme();
  const [showDelete, toggleDelete] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    getInvoiceTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNew = () => navigate("/expenses-new-invoice");

  const handleNewPayment = () => {
    if (!selected) {
      setNotify(
        <AlertNotification
          type="info"
          message="No invoice selection found. Please make selection then try again."
          closable={false}
          iconClass="fa fa-info-circle"
        />
      );
      return;
    }
    openSettings("Settle Supplier Invoice", "SettleSupplierInvoice", selected, "Settle Payment");
  };

  const handleDelete = () => {
    if (!selected) {
      setNotify(
        <AlertNotification
          type="info"
          message="No invoice selection found. Please make selection then try again."
          closable={false}
          iconClass="fa fa-info-circle"
        />
      );
      return;
    }
    toggleDelete(true);
  };

  const PageActions = () => (
    <>
      <ActionDropdownItem iconClass="fa fa-circle-check text-success" label="Settle Invoice" onClick={handleNewPayment} />
      <ActionDropdownItem iconClass="fa fa-edit text-primary" label="New Invoice" onClick={handleNew} />
      <ActionDropdownItem iconClass="fa fa-trash-alt" label="Delete Invoice" onClick={handleDelete} className="text-danger" />
      <ActionDropdownItem iconClass="fa fa-refresh" label="Refresh" onClick={refresh} />
    </>
  );

  const filteredTransactions = useMemo(() => {
    const q = String(searchText || "").trim().toLowerCase();
    if (!Array.isArray(incomingInvoiceListing)) return [];
    if (!q) return incomingInvoiceListing;

    return incomingInvoiceListing.filter((t) => {
      const supplier = String(t?.vendor?.title || "").toLowerCase();
      const ref = String(t?.invoice_ref || "").toLowerCase();
      return supplier.includes(q) || ref.includes(q);
    });
  }, [incomingInvoiceListing, searchText]);

  return (
    <>
      <PageContainer>
        <MainContent>
          <PageTitle
            title="Supplier Invoices"
            description="List of all suppliers/creditors invoice records"
            iconClass="fa fa-arrow-up"
            PageActions={PageActions}
            sticky={true}
            Pagination={
              metaData ? (
                <Pagination
                  metaData={metaData}
                  onPageChange={(page) => setCurrentPage(page)}
                  loadMore={false}
                />
              ) : (
                <></>
              )
            }
          />

          {/* ✅ FilterBar */}
          <FilterBar
            title="Invoice listing"
            subtitle="Search invoices by supplier name"
            controls={[
              {
                type: "search",
                value: searchText,
                placeholder: "Type supplier name or invoice ref...",
                width: 320,
                onChange: (e) => setSearchText(e.target.value),
                onEnter: () => {}, // optional (filter is already reactive)
              },
            ]}
            actions={[
              { label: "New Invoice", iconClass: "fa fa-plus", onClick: handleNew, variant: "primary" },
            
            ]}
          />

          <div className="card mb-3">
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-hover table-striped table-bordered">
                  <thead className="thead-light">
                    <tr>
                      <th className="nowrap">Invoice Date</th>
                      <th>Supplier</th>
                      <th>Invoice Ref</th>
                      <th>Description</th>
                      <th>Amount</th>
                      <th>Balance</th>
                      <th>Expense Account</th>
                      <th>L/R</th>
                      <th>Added On</th>
                      <th>Added By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTransactions.length > 0 &&
                      filteredTransactions.map((transaction, index) => (
                        <tr
                          key={index}
                          className={`link ${transaction.id === selected?.id ? "selected-row" : ""}`}
                          onClick={() => setSelected(transaction)}
                        >
                          <td className="nowrap">{functions.formatDate(transaction.invoice_date)}</td>
                          <td>{transaction.vendor.title}</td>
                          <td>{transaction.invoice_ref}</td>
                          <td>{transaction.description}</td>
                          <td>{functions.numberFormat(transaction.amount)}</td>
                          <td>{functions.numberFormat(transaction.invoice_balance)}</td>
                          <td>{transaction.expense_account.account_name}</td>
                          <td>{transaction.ledger_id}</td>
                          <td>{functions.formatDate(transaction.created_on)}</td>
                          <td>{transaction.created_by.username}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>

                {filteredTransactions.length === 0 && (
                  <Notification message="No invoice records found" closable={false} />
                )}

                {!incomingInvoiceListing && <ActivityLoader width={20} />}
              </div>
            </div>
          </div>
        </MainContent>
      </PageContainer>

      {selected && (
        <AlertDeleteLedger
          title="Delete Invoice"
          alertDescription={`Please confirm that you want to delete the selected invoice: ${selected.invoice_ref}. This action is irreversible.`}
          show={showDelete}
          handleClose={() => toggleDelete(false)}
          selection={[selected.ledger_id]}
          notes="All ledger entries associated with this invoice will be deleted."
        />
      )}
    </>
  );
};

export default SupplierInvoicingPage;
